import { useEffect, useState } from 'react';
import axiosNest from '../../api/axiosNest';
import type { VehicleCandidate } from './suggestionsApi';

/**
 * Pozycja aut z krótkiej listy (warianty, „Obsadź / Zmień obsadzenie”)
 * w podpowiedziach aut pod zlecenie — status 29.09.2026.
 *
 * Odpowiada `GET /user-offers/:id/candidate-ranks`
 * (`apps/api/src/user-offers/candidate-ranks.ts`). To miejsce na liście
 * ułożonej po dolocie, liczone w chwili otwarcia karty — nie ocena. Liczenie
 * trwa 1–2 s, więc pyta tylko karta zlecenia, nigdy lista banku.
 */

export interface CandidateRank {
  carId: number;
  /** Miejsce na liście po dolocie, od 1; `null` = auta tam nie ma. */
  rank: number | null;
  /** Klucz powodu, gdy auta nie ma na liście rekomendacji (dane, nie do pokazania). */
  outOfList: string | null;
  /** Ten powód po polsku, z API (07.10.2026); brak = starsze API. */
  outOfListText?: string | null;
  origin: string | null;
  addedManually: boolean;
  /**
   * Wiersz kandydata z tego samego przeliczenia co lista rekomendacji
   * (06.10.2026): dolot, koszt, marża, kierowca, zapas, rozładunek, uwagi,
   * podstawa. `null` = nie da się policzyć (brak pozycji, naczepa, odmowa
   * dla zlecenia); brak pola = starsze API.
   */
  candidate?: VehicleCandidate | null;
}

export interface CandidateRanks {
  orderId: number;
  /** Długość pełnej listy podpowiedzi. */
  total: number | null;
  /** Podpowiedzi dla zlecenia nie ma w ogóle — z powodem. */
  refused: string | null;
  vehicles: CandidateRank[];
}

export async function fetchCandidateRanks(
  id: number | string,
): Promise<CandidateRanks> {
  const { data } = await axiosNest.get(`/user-offers/${id}/candidate-ranks`);
  return data as CandidateRanks;
}

/**
 * Powód spoza listy do pokazania. Zdanie przychodzi z API (`outOfListText`,
 * 07.10.2026: „mapa etykiet po stronie API, klucz zostaje, ekran dostaje
 * zdanie”) — dotąd front podpisywał tylko trzy klucze, a resztę („zajęty
 * w oknie zlecenia” w starszych mapach, „oznaczony jako niesprawny”)
 * pokazywał surowo. Starsze API bez zdania — sam klucz.
 */
export function outOfListLabel(rank: Pick<CandidateRank, 'outOfList' | 'outOfListText'>): string | null {
  return rank.outOfListText ?? rank.outOfList;
}

/**
 * Tekst pozycji: „3. miejsce na 41” albo „poza listą rekomendacji: <powód>”.
 * `null`, gdy nie ma czego pokazać (jeszcze się liczy, błąd, odmowa dla
 * całego zlecenia).
 */
export function rankText(
  rank: CandidateRank | undefined,
  total: number | null,
): string | null {
  if (!rank) return null;
  if (rank.rank !== null) {
    return total !== null ? `${rank.rank}. miejsce na ${total}` : `${rank.rank}. miejsce`;
  }
  const reason = outOfListLabel(rank);
  if (reason) {
    return `poza listą rekomendacji: ${reason}`;
  }
  return null;
}

/** Skąd wziął się wariant — tylko to, co wiemy; stary wiersz bez źródła = nic. */
export function originText(origin: string | null | undefined): string | null {
  switch (origin) {
    case 'manual':
      return 'dodany ręcznie';
    case 'assignment':
      return 'z obsadzenia (np. wpisanego w TMS)';
    case 'suggestion':
      return 'z rekomendacji';
    case 'order_suggestion':
      return 'z rekomendacji na karcie pojazdu';
    case 'external':
      return 'z OneHub';
    default:
      return null;
  }
}

/**
 * Dopisek przy pojeździe, który jest też wariantem innych zleceń: „wskazany
 * też do zlecenia: X” albo „… do zleceń: X, Y”. Konflikt do pokazania, nie
 * blokada — wspólny dla tabeli wariantów i listy rekomendacji.
 */
export function otherOrdersText(
  orders: ReadonlyArray<{ userOfferId: number; externalId: string | null }>,
): string {
  const numbers = orders.map((o) => o.externalId ?? `#${o.userOfferId}`);
  return `wskazany też do ${numbers.length === 1 ? 'zlecenia' : 'zleceń'}: ${numbers.join(', ')}`;
}

/**
 * Pozycje dla zlecenia. `key` zmienia się, gdy zmienia się krótka lista
 * (dodany albo zwolniony wariant, obsadzenie) — wtedy pytamy od nowa;
 * serwer i tak trzyma wynik minutę. Pusty `key` = nie ma o co pytać.
 * Błąd zostawia `null` — pozycja to dodatek, karta działa bez niej.
 */
export function useCandidateRanks(
  id: number | string | undefined,
  key: string,
): CandidateRanks | null {
  const [ranks, setRanks] = useState<CandidateRanks | null>(null);
  useEffect(() => {
    let alive = true;
    setRanks(null);
    if (id === undefined || key === '') return undefined;
    (async () => {
      try {
        const result = await fetchCandidateRanks(id);
        if (alive) setRanks(result);
      } catch {
        // Pozycji nie pokazujemy — reszta karty zostaje.
      }
    })();
    return () => {
      alive = false;
    };
  }, [id, key]);
  return ranks;
}

/** Klucz krótkiej listy: auta wariantów (ze stanem) i obsadzone auto. */
export function shortListKey(
  candidates: ReadonlyArray<Record<string, unknown>>,
  assignedCarId: unknown,
): string {
  const parts = candidates.map((c) => `${String(c.carId)}:${String(c.status)}`);
  if (assignedCarId != null) parts.push(`assigned:${String(assignedCarId)}`);
  return parts.sort().join(',');
}

/**
 * Czy odpowiedź niesie wyliczenia przy autach (06.10.2026). Starsze API
 * bez pola `candidate` i odmowa dla całego zlecenia — nie: tabela wariantów
 * wygląda wtedy jak dotąd, bez kolumn wyliczeń.
 */
export function hasCandidateDetails(ranks: CandidateRanks | null): boolean {
  if (ranks === null || ranks.refused) return false;
  return ranks.vehicles.some((v) => v.candidate !== undefined);
}

/** Pozycja auta po id — wygodnie dla wierszy tabeli i listy wyboru. */
export function rankFor(
  ranks: CandidateRanks | null,
  carId: unknown,
): CandidateRank | undefined {
  return ranks?.vehicles.find((v) => v.carId === Number(carId));
}
